import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ROTATE_MS = 5000;

const OfferSlide = ({ offer, discountText, limitedLabel, endsLabel, tapLabel }) => (
  <Link
    to={`/marketplace/offers/${offer._id}`}
    className="block bg-gradient-to-r from-glow-600/25 via-purple-600/10 to-emerald-600/20 p-6 outline-none ring-glow-500/40 transition-[box-shadow] hover:ring-2 focus-visible:ring-2"
  >
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-xs text-zinc-700 dark:text-zinc-200/80">{limitedLabel}</p>
          {offer.salonName && (
            <span className="rounded-full bg-black/10 px-2 py-0.5 text-xs font-medium dark:bg-white/10">
              {offer.salonName}
            </span>
          )}
        </div>
        <p className="mt-1 truncate text-xl font-semibold">{offer.bannerText || offer.name}</p>
        {offer.endAt && (
          <p className="mt-1 text-xs text-zinc-700 dark:text-zinc-200/70">{endsLabel}</p>
        )}
        <p className="mt-2 text-sm font-medium text-glow-400">{tapLabel}</p>
      </div>
      <span className="shrink-0 rounded-full bg-black/10 px-4 py-2 text-sm font-semibold text-zinc-900 dark:bg-white/10 dark:text-zinc-100">
        {discountText}
      </span>
    </div>
  </Link>
);

const MarketplaceOffersCarousel = ({ campaigns }) => {
  const { t, i18n } = useTranslation();
  const count = campaigns.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const discountText = (offer) =>
    offer.discountMode === 'PERCENTAGE'
      ? t('marketplace.percentOff', { value: Number(offer.discountValue) })
      : t('marketplace.flatOff', { value: Number(offer.discountValue || 0).toLocaleString() });

  const endsLabel = (offer) =>
    offer.endAt
      ? t('marketplace.ends', { date: new Date(offer.endAt).toLocaleString(i18n.language) })
      : null;

  useEffect(() => {
    setIndex(0);
  }, [campaigns]);

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [count, paused]);

  if (!count) return null;

  const multi = count > 1;
  const slideProps = {
    limitedLabel: t('marketplace.limitedOffer'),
    tapLabel: t('marketplace.tapForDetails'),
  };

  return (
    <div
      className="relative mb-6"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="relative overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800">
        {multi && (
          <div className="invisible pointer-events-none" aria-hidden>
            <OfferSlide
              offer={campaigns[index]}
              discountText={discountText(campaigns[index])}
              endsLabel={endsLabel(campaigns[index])}
              {...slideProps}
            />
          </div>
        )}
        {campaigns.map((offer, i) => (
          <div
            key={offer._id}
            className={
              multi
                ? `absolute inset-0 transition-opacity duration-500 ease-in-out ${
                    i === index ? 'z-10 opacity-100' : 'z-0 opacity-0 pointer-events-none'
                  }`
                : ''
            }
            aria-hidden={multi && i !== index}
          >
            <OfferSlide
              offer={offer}
              discountText={discountText(offer)}
              endsLabel={endsLabel(offer)}
              {...slideProps}
            />
          </div>
        ))}
      </div>

      {multi && (
        <div className="mt-3 flex items-center justify-center gap-2">
          {campaigns.map((offer, i) => (
            <button
              key={offer._id}
              type="button"
              aria-label={t('marketplace.showOffer', { n: i + 1, name: offer.name })}
              aria-current={i === index ? 'true' : undefined}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all ${
                i === index ? 'w-6 bg-glow-500' : 'w-2 bg-zinc-400 dark:bg-zinc-600 hover:bg-zinc-500'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketplaceOffersCarousel;
